import { ReactNode } from 'react'

import { Box, Divider, Grid, List as MuiList, ListItemButton as MuiListItemButton, Typography } from '@mui/material'
import { useTheme } from '@mui/material/styles'
import { Loading } from 'components/ui'
import { useDateTime, useMappedTranslation, usePageTitle } from 'hooks'
import IconArrow from 'pages/policies/assets/images/arrow.svg'
import { Consent } from 'pages/policies/utils'

type Props = {
  title: string | ReactNode
  titleIcon?: string
  isLoading: boolean
  detail?: string
  consents?: Consent[]
  action?: ReactNode
  isPublishTitle?: boolean
  handleListButton: (item: any) => void
  showNewButton?: boolean
  showOldButton?: boolean
}
export default function List({
  title,
  titleIcon,
  isLoading,
  consents,
  action,
  isPublishTitle = false,
  handleListButton,
  showNewButton,
  showOldButton
}: Props) {
  const dateTime = useDateTime()
  const theme = useTheme()
  const t = useMappedTranslation()
  usePageTitle(typeof title === 'string' ? title : undefined)

  return (
    <Box sx={{ width: '100%', height: '100%' }}>
      {isLoading ? (
        <Loading fullScreen />
      ) : (
        <>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              textAlign: 'center'
            }}
          >
            {titleIcon && <Box component='img' src={titleIcon} sx={{ width: '48px', height: '48px', mb: '10px' }} alt='' />}
          </Box>
          <Box sx={{ width: '100%', maxWidth: '1040px' }}>
            <Typography
              variant='h1'
              fontWeight='500'
              fontSize={{ xs: '36px', md: '48px' }}
              color={theme.palette.mode === 'light' ? '#252525' : '#FAFAFA'}
              textAlign='center'
              mx={{ xs: '24px', md: '0' }}
            >
              {title}
            </Typography>
          </Box>
          <Box sx={{ width: '100%', mt: { xs: '125px', md: '112px' } }}>
            {consents && consents.length >= 1 && (
              <>
                <MuiList sx={{ padding: '0' }}>
                  <Divider />
                  {showNewButton && (
                    <Box sx={{ width: '100%' }}>
                      <MuiListItemButton
                        sx={{ padding: '0px' }}
                        onClick={() => handleListButton('new')}
                        data-testid='MuiListItemButtonFD8E609E'
                      >
                        <Grid
                          container
                          wrap={'nowrap'}
                          justifyContent='space-between'
                          sx={{ padding: { xs: '30px 24px', md: '37px 0' } }}
                        >
                          <Typography
                            variant='body1'
                            color={theme.palette.mode === 'dark' ? '#fefefe' : '#010101'}
                            sx={{ fontSize: '20px', textAlign: 'start' }}
                          >
                            {t('iam.01.Common.Button.New.versions')}
                          </Typography>
                          <Box
                            sx={{
                              minWidth: '24px',
                              height: '24px',
                              display: 'flex',
                              justifyContent: 'center',
                              alignItems: 'center',
                              marginInlineStart: '12px'
                            }}
                          >
                            <Box component='img' src={IconArrow} alt='' />
                          </Box>
                        </Grid>
                      </MuiListItemButton>
                      <Divider />
                    </Box>
                  )}
                  {consents.map(
                    (item, index) =>
                      (item.root === undefined || !item.root) && (
                        <Box key={item.id} sx={{ width: '100%' }}>
                          <MuiListItemButton
                            sx={{ padding: '0px' }}
                            onClick={() => handleListButton(item)}
                            data-testid='MuiListItemButton0D50E6C6'
                          >
                            <Grid
                              container
                              wrap={'nowrap'}
                              justifyContent='space-between'
                              sx={{ padding: { xs: '30px 24px', md: '37px 0' } }}
                            >
                              <Typography
                                variant='body1'
                                color={theme.palette.mode === 'dark' ? '#fefefe' : '#010101'}
                                sx={{ fontSize: '20px', textAlign: 'start' }}
                              >
                                {isPublishTitle ? dateTime(Number(item.publish), { format: 'withoutTime' }) : item.title}
                              </Typography>
                              <Box
                                sx={{
                                  minWidth: '24px',
                                  height: '24px',
                                  display: 'flex',
                                  justifyContent: 'center',
                                  alignItems: 'center',
                                  marginInlineStart: '12px'
                                }}
                              >
                                <Box component='img' src={IconArrow} alt='' />
                              </Box>
                            </Grid>
                          </MuiListItemButton>
                          {consents.length === index + 1 ? '' : <Divider />}
                        </Box>
                      )
                  )}
                  {showOldButton && (
                    <Box sx={{ width: '100%' }}>
                      <Divider />
                      <MuiListItemButton
                        sx={{ padding: '0px' }}
                        onClick={() => handleListButton('old')}
                        data-testid='MuiListItemButton94A478B6'
                      >
                        <Grid
                          container
                          wrap={'nowrap'}
                          justifyContent='space-between'
                          sx={{ padding: { xs: '30px 24px', md: '37px 0' } }}
                        >
                          <Typography
                            variant='body1'
                            color={theme.palette.mode === 'dark' ? '#fefefe' : '#010101'}
                            sx={{ fontSize: '20px', textAlign: 'start' }}
                          >
                            {t('iam.01.Common.Button.Previous.versions')}
                          </Typography>
                          <Box
                            sx={{
                              minWidth: '24px',
                              height: '24px',
                              display: 'flex',
                              justifyContent: 'center',
                              alignItems: 'center',
                              marginInlineStart: '12px'
                            }}
                          >
                            <Box component='img' src={IconArrow} alt='' />
                          </Box>
                        </Grid>
                      </MuiListItemButton>
                    </Box>
                  )}
                </MuiList>
                <Divider />
              </>
            )}
          </Box>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              mt: { xs: '52px', md: '72px' }
            }}
          >
            {action}
          </Box>
        </>
      )}
    </Box>
  )
}
